import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { encode } from "base64-arraybuffer";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

function ListadoInventariosGeneral() {
  const { user } = useContext(AuthContext);

  const URL_INVENTARIOS = `${process.env.REACT_APP_URL_INVENTARIOS}`;
  const URL_SEND_MAIL_ATT = process.env.REACT_APP_URL_SEND_MAIL_ATT;

  const [inventarioGeneral, setInventarioGeneral] = useState(0);
  const [categorias, setCategorias] = useState([]);

  const [modalMail, setModalMail] = useState(false);
  const toggleMail = () => setModalMail(!modalMail);
  const [mailTo, setMailTo] = useState("");

  const [comments, setComments] = useState([]);
  const [comments2, setComments2] = useState([]);

  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

  const money = (value) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const number = (value) =>
    new Intl.NumberFormat("en-US").format(Number(value || 0));

  useEffect(() => {
    showLoader();

    axios
      .get(`${URL_INVENTARIOS}InventariosGeneral`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setInventarioGeneral(response.data?.inventarioGeneral || 0);
        setCategorias(response.data?.categorias || []);
        setComments(response.data?.detalle || []);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();

        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "No se pudo cargar el inventario general",
        });
      });
  }, []);

  const headers = [
    { name: "ITEM", field: "item", sortable: true },
    { name: "Nombre", field: "nombre", sortable: true },
    {
      name: "Inventario de bodegas propias",
      field: "inventarioPropias",
      sortable: true,
    },
    {
      name: "Bodegas de plataforma",
      field: "bodegasPlataforma",
      sortable: true,
    },
    { name: "Costo", field: "costo", sortable: true },
    { name: "Valor de Inventario", field: "valorInventario", sortable: true },
    { name: "% de inventario", field: "porcentajeInventario", sortable: true },
  ];

  const numericFields = [
    "inventarioPropias",
    "bodegasPlataforma",
    "costo",
    "valorInventario",
    "porcentajeInventario",
  ];

  const commentsData = useMemo(() => {
    let computedComments = [...comments];

    if (search) {
      const s = search.toLowerCase();

      computedComments = computedComments.filter(
        (comment) =>
          String(comment.item || "").toLowerCase().includes(s) ||
          String(comment.nombre || "").toLowerCase().includes(s) ||
          String(comment.categoria || "").toLowerCase().includes(s) ||
          String(comment.inventarioPropias || "").includes(search) ||
          String(comment.bodegasPlataforma || "").includes(search) ||
          String(comment.costo || "").includes(search) ||
          String(comment.valorInventario || "").includes(search) ||
          String(comment.porcentajeInventario || "").includes(search)
      );
    }

    setTotalItems(computedComments.length);
    setComments2(computedComments);

    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;

      computedComments.sort((a, b) => {
        if (numericFields.includes(sorting.field)) {
          return (
            reversed *
            (Number(a[sorting.field] || 0) - Number(b[sorting.field] || 0))
          );
        }

        return (
          reversed *
          String(a[sorting.field] || "").localeCompare(
            String(b[sorting.field] || ""),
            "es",
            { sensitivity: "base" }
          )
        );
      });
    }

    return computedComments.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting]);

  function PDFTabla() {
    const doc = new jsPDF("landscape", "mm", "a4", true);

    doc.setFontSize(16);
    doc.text("Inventario General", 14, 15);
    doc.text(money(inventarioGeneral), 105, 15);

    const categoriasBody = categorias.map((a) => [
      a.categoria,
      money(a.valor),
    ]);

    doc.autoTable({
      body: categoriasBody,
      startY: 25,
      theme: "grid",
      styles: {
        fontSize: 9,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 70 },
        1: { halign: "right", cellWidth: 55 },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 12;

    doc.setFontSize(12);
    doc.text("Detalle de Inventario", 14, finalY);

    const body = comments2.map((a) => [
      a.item,
      a.nombre,
      number(a.inventarioPropias),
      number(a.bodegasPlataforma),
      money(a.costo),
      money(a.valorInventario),
      `${number(a.porcentajeInventario)}%`,
    ]);

    doc.autoTable({
      head: [
        [
          "ITEM",
          "Nombre",
          "Inventario de bodegas propias",
          "Bodegas de plataforma",
          "Costo",
          "Valor de Inventario",
          "% de inventario",
        ],
      ],
      body,
      startY: finalY + 5,
      theme: "grid",
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "right", cellWidth: 20 },
        1: { cellWidth: 55 },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
      },
    });

    doc.save("InventarioGeneral.pdf");
  }

  function excel() {
    const dataExcel = comments2.map((a) => ({
      ITEM: a.item,
      Nombre: a.nombre,
      InventarioBodegasPropias: a.inventarioPropias,
      BodegasPlataforma: a.bodegasPlataforma,
      Costo: a.costo,
      ValorInventario: a.valorInventario,
      PorcentajeInventario: a.porcentajeInventario,
      Categoria: a.categoria,
    }));

    const option = {
      fileName: "InventarioGeneral",
      datas: [
        {
          sheetData: dataExcel,
          sheetName: "InventarioGeneral",
          sheetFilter: [
            "ITEM",
            "Nombre",
            "InventarioBodegasPropias",
            "BodegasPlataforma",
            "Costo",
            "ValorInventario",
            "PorcentajeInventario",
            "Categoria",
          ],
          sheetHeader: [
            "ITEM",
            "Nombre",
            "Inventario de bodegas propias",
            "Bodegas de plataforma",
            "Costo",
            "Valor de Inventario",
            "% de inventario",
            "Categoría",
          ],
        },
      ],
    };

    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  function enviaMail() {
    const doc = new jsPDF("landscape", "mm", "a4", true);

    doc.setFontSize(16);
    doc.text("Inventario General", 14, 15);
    doc.text(money(inventarioGeneral), 105, 15);

    doc.autoTable({
      body: categorias.map((a) => [a.categoria, money(a.valor)]),
      startY: 25,
      theme: "grid",
      styles: {
        fontSize: 9,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 70 },
        1: { halign: "right", cellWidth: 55 },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 12;

    doc.setFontSize(12);
    doc.text("Detalle de Inventario", 14, finalY);

    doc.autoTable({
      head: [
        [
          "ITEM",
          "Nombre",
          "Inventario de bodegas propias",
          "Bodegas de plataforma",
          "Costo",
          "Valor de Inventario",
          "% de inventario",
        ],
      ],
      body: comments2.map((a) => [
        a.item,
        a.nombre,
        number(a.inventarioPropias),
        number(a.bodegasPlataforma),
        money(a.costo),
        money(a.valorInventario),
        `${number(a.porcentajeInventario)}%`,
      ]),
      startY: finalY + 5,
      theme: "grid",
      styles: {
        fontSize: 8,
      },
    });

    const att = doc.output("arraybuffer");
    const base64File = encode(att);

    axios
      .post(
        URL_SEND_MAIL_ATT,
        {
          subject: "Inventario General",
          email: mailTo,
          fileName: "InventarioGeneral.pdf",
          att: base64File,
          text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
           <th><img width="${process.env.REACT_APP_TAMANO_LOGO_MAIL}" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}logoInstitucion.png" /></th>
           <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${process.env.REACT_APP_NOMBRE_INSTITUCION}</tr>
           <tbody>
           <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
           <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Adjunto encontrarás el Inventario General.</tr>
           </tbody>
           <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo.</tr>
           </table>`,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then(() => {
        toggleMail();
        Swal.fire("Good job!", "Enviado con éxito", "success");
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          footer: `${error.response?.data || ""}`,
        });
        console.log(error);
      });
  }

  let totalPropias = 0;
  let totalPlataforma = 0;
  let totalValor = 0;

  return (
    <>
      <Header />
      <br />
      <br />

      {user.inventarios ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuInventarios"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>

            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button
                  size="sm"
                  className="btn"
                  color="danger"
                  onClick={PDFTabla}
                >
                  PDF <i className="far fa-file-pdf"></i>
                </Button>

                <Button
                  size="sm"
                  className="btn"
                  onClick={toggleMail}
                  color="info"
                >
                  eMail <i className="fas fa-at"></i>
                </Button>

                <Button
                  size="sm"
                  className="btn"
                  color="primary"
                  onClick={excel}
                >
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <Modal size="sm" isOpen={modalMail} toggle={toggleMail}>
            <ModalHeader toggle={toggleMail}>
              <h4>Enviar Inventario General</h4>
            </ModalHeader>

            <ModalBody>
              <Label className="mr-sm-2">Email</Label>
              <Input
                className="col-sm-12"
                type="text"
                value={mailTo}
                required
                onChange={(e) => {
                  setMailTo(e.target.value);
                }}
              />
            </ModalBody>

            <ModalFooter>
              <Button size="sm" color="success" onClick={enviaMail}>
                Enviar
              </Button>
            </ModalFooter>
          </Modal>

          <h3 align="center">Inventario General</h3>

          <Row>
            <Col md={4}>
              <Table size="sm" bordered>
                <tbody>
                  <tr>
                    <td className="negrita" style={{ fontSize: "18px" }}>
                      Inventario General
                    </td>
                    <td
                      className="negrita"
                      align="right"
                      style={{ fontSize: "18px" }}
                    >
                      {money(inventarioGeneral)}
                    </td>
                  </tr>

                  {categorias.map((a) => (
                    <tr key={a.categoria}>
                      <td className="negrita">{a.categoria}</td>
                      <td align="right">{money(a.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>

          <br />

          <h5 className="negrita">Detalle de Inventario</h5>

          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>

            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <br />

          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />

            <tbody>
              {commentsData.map((a) => {
                totalPropias += Number(a.inventarioPropias || 0);
                totalPlataforma += Number(a.bodegasPlataforma || 0);
                totalValor += Number(a.valorInventario || 0);

                return (
                  <tr key={a.idArticulo}>
                    <td>{a.item}</td>
                    <td>{a.nombre}</td>
                    <td>{number(a.inventarioPropias)}</td>
                    <td>{number(a.bodegasPlataforma)}</td>
                    <td>{money(a.costo)}</td>
                    <td>{money(a.valorInventario)}</td>
                    <td>
                      {number(a.porcentajeInventario)}%
                    </td>
                  </tr>
                );
              })}

              <tr>
                <td></td>
                <td className="negrita">TOTAL GENERAL</td>
                <td className="negrita">
                  {number(
                    comments2.reduce(
                      (sum, a) => sum + Number(a.inventarioPropias || 0),
                      0
                    )
                  )}
                </td>
                <td className="negrita">
                  {number(
                    comments2.reduce(
                      (sum, a) => sum + Number(a.bodegasPlataforma || 0),
                      0
                    )
                  )}
                </td>
                <td></td>
                <td className="negrita">
                  {money(
                    comments2.reduce(
                      (sum, a) => sum + Number(a.valorInventario || 0),
                      0
                    )
                  )}
                </td>
                <td></td>
              </tr>
            </tbody>
          </Table>

          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}

      {loader}
    </>
  );
}

export default ListadoInventariosGeneral;